import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import * as kb3d from '@kb3d';
import {
    applyTransformToSpace,
    IKb3dPointerArgs,
    IKeyboardArgs,
    isAnnotation,
    isSpaceNode,
    Kb3dObject,
    KbColor,
    KbQuaternion,
    MaterialNode,
    SceneNode,
    SpaceNode
} from '@kb3d';
import {
    Configurator,
    eAlertType,
    eColorFormat,
    eEnvironment,
    eHotspotShape,
    eRuleType,
    Field,
    Hotspot,
    IColorOperationArgs,
    IGetTableRuleArgs,
    IHotspotClickArgs,
    IJsResult,
    IKb3dActivateGizmoArgs,
    IKb3dActivateSketchArgs,
    IKb3dAdBoundingboxWatcherArgs,
    IKb3dAddClickHandlerArgs,
    IKb3dAddHotspotClickHandlerArgs,
    IKb3dAnimateArgs,
    IKb3dCallSceneFunctionArgs,
    IKb3dClearHandlerArgs,
    IKb3dCloneNodeArgs,
    IKb3dCreateNodeArgs,
    IKb3dCreateSketchPathArgs,
    IKb3dGetChildrenOfTypeArgs,
    IKb3dGetPropertyArgs,
    IKb3dHighlightArgs,
    IKb3dSetPropertyArgs,
    ISceneRuleArgs,
    ISetParentArgs,
    KbObjectManager,
    UiObject,
} from '@models';
import { ClientLogger, KPromise } from '@rules';
import { Color, IMessage, Utils } from '@tools';
import { ILoadSceneArgs, ISceneViewerArgs, SceneViewer } from './scene-viewer';

const SCENE_CLICK_HANDLER_TAG = 'scene_click';
export class Kb3dViewer extends SceneViewer {
    constructor(private cArgs: ISceneViewerArgs) {
        super(cArgs);
        this.enableScreenshots = cArgs.enableScreenshots || false;
        //this.sceneService = args.$injector.get(Token.ClaraSceneService.key);
        //this.$scope = cArgs.controllerScope.$new();
        this.$scope.hotspotClick = ($event, ann) => this.hotspotClick($event, ann);
        this.$scope.hotspotCloseClick = ($event, ann) => this.hotspotCloseClick($event, ann);
        this.$scope.arActive = () => this.viewer.ar.xrActive;
        this.$scope.toggleAr = () => this.toggleAr();
        this.$scope.arEnabled = true;
        Utils.supportsVr().then(res => (this.$scope.supportsVr = res));
        this.$scope.allHotspots = [];
    }

    viewer: kb3d.KbViewer;
    sceneNode: kb3d.SceneNode;
    lastDeploy: number;
    enableScreenshots: boolean;

    public init() {
        this.createElem();

        this.viewer = new kb3d.KbViewer({
            container: this.elem,
            highlightColor: this.highlightColor ? KbColor.FromIntObject(new Color(this.highlightColor).toRgb()) : null,
            webworkerPath:
                this.clusterEnv === 'dev'
                    ? `${this.assetRoot}/worker3d.js`
                    : `${this.assetRoot}/worker3d.min.js?v=${this.cacheBuster}`,
            debugMode: this.clusterEnv === 'dev',
            forceDisableGraphicsOptimization: this.isRender,
            enableScreenshots: this.enableScreenshots,
        });

        this.elem.addEventListener('kb3d.scenePropertyChange', (e: CustomEvent) => {
            if ('arEnabled' in e.detail) {
                const arEnabled = e.detail['arEnabled'];
                this.$scope.$apply(() => {
                    this.$scope.arEnabled = arEnabled;
                });
            }
        });

        this.createHotspotElem();
        this.viewer.viewChanged.subscribe(() => this.updateHotspots(true));
        this.viewer.interaction.onKeyboard.subscribe(k => this.onKeyboard(k));

        return new KPromise(null) as any as ng.IPromise<any>;
    }

    public dispose() {
        if (this.viewer) {
            this.viewer.gizmos.clearGizmo();
            this.viewer.dispose();
        }
        if (this.sceneNode && this.sceneNode._manager) {
            this.sceneNode._manager.clickHandlers.deleteByTag(SCENE_CLICK_HANDLER_TAG);
        }
        if (this.elem) {
            this.elem.remove();
            this.elem = null;
        }
    }

    public loadSceneInternal(args: ILoadSceneArgs): ng.IPromise<void> {
        let manager = new kb3d.Kb3dManager({
            assetRoot: this.assetRoot,
            forceFullSizeTextures: args.isRenderContext || false,
            environment: {
                lastDeploy: this.lastDeploy,
                cacheBuster: this.cacheBuster,
            },
        }); //default services automatically used
        this.sceneNode = manager.deserialize<kb3d.SceneNode>(this.scene.graph);
        this.$scope.arEnabled = !!this.sceneNode.arEnabled;
        this.$scope.fullscreenEnabled = !!this.sceneNode.fullscreenEnabled;
        args.config.$sceneNode = this.sceneNode;
        this.sceneNode._configurator = args.config;

        //args.loadingExperience is set to false in render.ts to disable loading experiences for renders
        if (args.loadingExperience === false) {
            this.viewer.loadScene({
                sceneNode: this.sceneNode,
                loadingExperience: kb3d.eLoadingExperience.none,
                deferLoadingPromise: this.$q.all([this.loadDeferred.promise, this.configuratorLoadedPromise]) as any,
            });
        } else {
        this.viewer.loadScene({
            sceneNode: this.sceneNode,
            deferLoadingPromise: this.$q.all([this.loadDeferred.promise, this.configuratorLoadedPromise]) as any,
        });
    }

        manager.clickHandlers.add(this.sceneNode, SCENE_CLICK_HANDLER_TAG, p => {
            this.onSceneClick(p);
        });

        return this.runRules(false).then(() => {
            this.notifyLoaded();
        });
    }

    public runRules(waitForLoad = true): ng.IPromise<any> {
        let promise: ng.IPromise<any> = waitForLoad ? this.loadDeferred.promise : (new KPromise(null) as any);

        return promise
            .then(() => {
                let ruleArgs = this.getRuleArgs(eRuleType.scene);
                this.setFieldsIfStandalone();

                // before running the value rules, we need to clear out transient handlers
                this.clearTransientHandlers(this.sceneNode, eRuleType.value);

                let runUiRules = !this.sceneDb[this.sceneConfig.idScene].idProduct;
                if (runUiRules) {
                    // run the value rule first
                    // ruleArgs.sceneUtils.ruleType = eRuleType.value;
                    return this.runRuleTypeAsync(
                        this.sceneConfig,
                        eRuleType.value,
                        this.getRuleArgs(eRuleType.value)
                    ).then(() => {
                        this.sceneConfig.preValidate();
                        this.sceneConfig.isValid();
                    });
                }
            })
            .then(() => {
                return this.runSceneRules(this.sceneConfig, this.uiConfig, this.getRuleArgs(eRuleType.scene), false);
            })
            .then(result => {
                this.handleRuleError(eRuleType.scene, result);
                //recalculate all hotspots
                this.$scope.allHotspots = this.getAllHotspots();
                this.updateHotspots(false);

                return result;
            });
    }

    protected clearTransientHandlers(sceneNode: SceneNode, ...ruleTypes: eRuleType[]) {
        let m = sceneNode._manager;
        let handlerMaps = [m.clickHandlers, m.doubleClickHandlers, m.draggables, m.hotspotClickHandlers];
        for (let hm of handlerMaps) {
            hm.deleteByTag(...ruleTypes);
        }
    }

    protected runSceneRules(
        sceneConfig: Configurator,
        uiConfig: Configurator,
        ruleArgs: Kb3dSceneRuleArgs,
        skipRulesIfNoNestedSceneChanges: boolean
    ): ng.IPromise<IJsResult<ISceneRuleArgs>> {
        let nestedSceneRulesWereRun = false;

        return this.runNestedSceneRules(uiConfig)
            .then((r: any[]) => {
                nestedSceneRulesWereRun = r && r.some(r2 => r2 != null);
                if (!sceneConfig.$ranLoadedRule) {
                    sceneConfig.$ranLoadedRule = true;
                    skipRulesIfNoNestedSceneChanges = false;
                    ruleArgs.ruleType = eRuleType.sceneLoaded;
                    return this.runRuleTypeAsync(sceneConfig, eRuleType.sceneLoaded, ruleArgs);
                }
            })
            .then(() => {
                this.deleteOrphanedNestedScenes(uiConfig);
                if (!skipRulesIfNoNestedSceneChanges || nestedSceneRulesWereRun) {
                    //skip running the rules if there were no nested scene rules run
                    this.clearTransientHandlers(ruleArgs.sceneNode, eRuleType.scene);
                    ruleArgs.ruleType = eRuleType.scene;
                    return this.runRuleTypeAsync(sceneConfig, eRuleType.scene, ruleArgs).then(ruleResult => {
                        uiConfig.$fieldsDirtyForSceneRules = false;
                        return ruleResult;
                    });
                }
            });
    }

    public runNestedSceneRules(uiConfig: Configurator) {
        let nestedConfigs = uiConfig.getNestedConfigurators();

        if (nestedConfigs.length) {
            let promises: ng.IPromise<any>[] = [];
            // loop through nested configurators, merge the scenes if they haven't been already, and run their rules
            nestedConfigs.forEach(nested => {
                if (nested.idScene) {
                    let refConfig = uiConfig.referencedConfigurators.find(rc => rc.idProduct == nested.idProduct);
                    if (refConfig.nestScene) {
                        // create the scene configurator for the nested configurator if it doesn't already exist
                        // (or has been swapped for another scene)
                        if (!nested.$sceneConfigurator || nested.$sceneConfigurator.idScene != nested.idScene) {
                            nested.$sceneConfigurator = new Configurator(
                                new KbObjectManager(),
                                this.sceneDb[nested.idScene].configurator
                            );
                        }
                        nested.$sceneConfigurator.$uiConfigurator = nested;
                        nested.$nestHotspots = refConfig.nestHotspots;
                        // in case the nested scene is a standalone scene, we need to set it's fields from the uiConfig
                        nested.$sceneConfigurator.setFields(nested.getFieldsObject());

                        let skipRules =
                            !nested.$fieldsDirtyForSceneRules && (!nested.alwaysChild || !nested.alwaysChildOf);

                        this.nestScene(
                            // merge the scene if it hasn't already been merged
                            uiConfig.$sceneNode as kb3d.SceneNode,
                            nested
                        );

                        promises.push(
                            // now run the rules on the nested scene
                            this.runSceneRules(
                                nested.$sceneConfigurator,
                                nested,
                                this.getNestedSceneRuleArgs(nested, nested.$sceneConfigurator, eRuleType.scene),
                                skipRules
                            )
                        );
                    }
                }
            });
            return this.$q.all(promises);
        } else {
            return new KPromise(null) as any;
        }
    }

    protected nestScene(parentSceneNode: SceneNode, nested: Configurator) {
        if (!nested.$sceneNode) {
            //nested scene has already been created so skip
            let ser = this.sceneDb[nested.idScene];
            let nestedSceneNode = parentSceneNode._manager.nestScene(nested.name, parentSceneNode, ser.graph as any);
            nested.$sceneNode = nestedSceneNode;
            nestedSceneNode._configurator = nested;

            //add automatic click to navigate if enabled
            if (nested.$parentConfigurator.nestedSceneClickToSelect) {
                parentSceneNode._manager.clickHandlers.add(nestedSceneNode, undefined, () => {
                    this.navigateToElement(nested);
                });
            }
        }
    }

    public runRuleTypeAsync<T extends ISceneRuleArgs>(
        config: Configurator,
        ruleType: string,
        args?: T
    ): ng.IPromise<IJsResult<T>> {
        return this.ruleService.runRuleTypeAsync(config, ruleType, args).then(res => {
            this.handleRuleError(ruleType, res);
            return res;
        });
    }

    protected deleteOrphanedNestedScenes(uiConfig: Configurator) {
        let allNested = uiConfig.getNestedConfigurators();

        let sceneNode = uiConfig.$sceneNode as kb3d.SceneNode;
        if (sceneNode) {
            for (var i = sceneNode.scenes.length - 1; i >= 0; i--) {
                var ns = sceneNode.scenes[i];
                if (!allNested.some(n => n === ns._configurator)) {
                    this.destroyNestedScene(sceneNode, ns);
                }
            }
        }
    }

    protected destroyNestedScene(parentScene: kb3d.SceneNode, nestedScene: kb3d.SceneNode) {
        let nestedConfig = nestedScene._configurator as Configurator;
        if (nestedConfig) nestedConfig.$sceneNode = undefined;
        nestedScene._configurator = undefined;
        parentScene.scenes.remove(nestedScene);
        nestedScene._manager.dispose(); //dispose of the nested scene manager
    }

    public clearHandlers() {}

    public runAction(actionName: string): ng.IPromise<IJsResult<ISceneRuleArgs>> {
        let args = this.getRuleArgs(eRuleType.action);
        return this.runActionRule(actionName, args);
    }

    public runSceneFunction(args: IKb3dCallSceneFunctionArgs, config: Configurator) {
        let ruleArgs = null;
        if (config && config.$sceneConfigurator) {
            ruleArgs = this.getNestedSceneRuleArgs(
                config,
                config.$sceneConfigurator,
                eRuleType.function
            ) as Kb3dSceneFunctionRuleArgs;
        } else {
            ruleArgs = this.getRuleArgs(eRuleType.function) as Kb3dSceneFunctionRuleArgs;
        }
        ruleArgs.parameters = {};
        for (let p in args.parameters) {
            ruleArgs.parameters[p.toCamelCase()] = args.parameters[p];
        }

        let fn = null;
        if (config && config.$sceneConfigurator) {
            fn = config.$sceneConfigurator.functions.find(a => a.name.isEqual(args.name));
        }

        if (!fn) {
            fn = this.sceneConfig.functions.find(a => a.name.isEqual(args.name));
        }

        if (fn) {
            return this.ruleService.runRuleContainerAsync(fn, ruleArgs).then(r => {
                this.handleRuleError(fn.name + ' ' + fn.$type, r);
                return r;
            });
        } else {
            throw `Cannot find scene function with name ${args.name}`;
        }
    }

    public runFieldAction(elem: UiObject) {
        let args = this.getRuleArgs(eRuleType.field);
        return this.ruleService.runRuleContainerAsync(elem, args).then(res => {
            this.handleRuleError(elem.name + ' ' + elem.$type, res);
            return res;
        });
    }

    public onSceneClick(p: IKb3dPointerArgs) {
        this.clearHotspots();
        this.$scope.$digest();
    }

    public onKeyboard(k: IKeyboardArgs) {
        let args = this.getRuleArgs(eRuleType.action);
        args['keyArgs'] = k;

        return this.ruleService
            .runRuleTypeAsync(this.sceneConfig, eRuleType.keyboard, args as ISceneRuleArgs)
            .then(r => {
                this.handleRuleError(eRuleType.keyboard, r);
                this.$scope.$digest();
            });
    }

    public importUploadFieldImage(uiConfig: Configurator, field: Field, file?: File): ng.IPromise<any> {
        return undefined;
    }

    public show() {
        super.show();
        this.viewer.startRender();
    }

    public hide() {
        this.viewer.stopRender();
        super.hide();
    }

    public resize() {}

    public snapshot(args: kb3d.ISnapshotArgs) {
        if (args['camera'] && !args.viewpoint) args.viewpoint = args['camera']; //backwards compatibility for old snapshot
        return this.viewer.snapshot.take(args);
    }

    protected toggleAr() {
        let rulesFn = (rt: eRuleType) => {
            return this.runRuleTypeAsync(this.sceneConfig, rt, this.getRuleArgs(rt)).then(() => {
                return this.runRules(false);
            });
        };

        if (this.arLoading.active()) {
            return;
        }

        let ruleType = this.$scope.arActive() ? eRuleType.xrExit : eRuleType.xrEnter;
        if (ruleType == eRuleType.xrEnter) {
            let subscription = this.viewer.ar.onSessionEnd.subscribe(() => {
                subscription.unsubscribe();
                return rulesFn(eRuleType.xrExit);
            });
            let overlayElem = document.querySelector('.xr-overlay') as HTMLElement | null;
            const arPromise = this.viewer.ar
                .start(overlayElem, () => rulesFn(eRuleType.xrEnter))
                .catch(err => {
                    if (this.cArgs.dialogService) {
                        this.$scope.$apply(() => {
                            this.cArgs.dialogService.alert({
                                type: eAlertType.error,
                                msg: 'Could not enter AR: ' + err,
                                persist: true,
                            });
                        });
                    } else {
                        console.warn('No Dialog Service!');
                        console.error(err);
                    }
                });
            this.arLoading.addPromise(arPromise);
            return arPromise;
        } else {
            return this.viewer.ar.end();
        }
    }

    protected updateHotspots(digest = true) {
        let hotspotProcessed = false;
        for (let hotspot of this.$scope.allHotspots) {
            const sceneNode = hotspot.$parentConfigurator?.$uiConfigurator?.$sceneNode as kb3d.SceneNode;
            if (sceneNode) {
                let sceneManager = sceneNode._manager;
                if (sceneManager) {
                    if (hotspot.target && hotspot.targetNode && hotspot.visible) {
                        hotspotProcessed = true;
                        let targetNode = sceneManager.getById<kb3d.MeshNode>(hotspot.targetNode);
                        if (targetNode) {
                            let pos = this.viewer.camera.getScreenBoundingBoxOfNode(targetNode);
                            if (pos) {
                                this.positionHotspot(pos, hotspot);
                            }
                        }
                    }
                    if (hotspot.targetShape == eHotspotShape.mesh && hotspot.targetNode) {
                        let eventTag = `hotspot_${hotspot.id}`;
                        let node = sceneManager.getById<kb3d.SpaceNode>(hotspot.targetNode);
                        if (node) {
                            if (hotspot.visible) {
                                if (!sceneManager.clickHandlers.has(node, eventTag)) {
                                    sceneManager.clickHandlers.add(node, eventTag, () =>
                                        this.hotspotClick({} as any, hotspot)
                                    );
                                }
                            } else {
                                //remove the click handler if the hotspot is invisible
                                sceneManager.clickHandlers.delete(node, eventTag);
                            }
                        }
                    }
                }
            }
        }
        // call digest manually on this scope instead of scope.$apply because $apply starts
        // at the root and digests everything.We only want to affect the hotspots here.
        if (hotspotProcessed && digest) {
            //this can be expensive, so only run if there is something to update
            this.$scope.$digest();
        }
    }

    protected clearHotspots() {
        for (let hotspot of this.$scope.allHotspots) {
            if (hotspot.target && hotspot.allowClose) {
                hotspot.open = false;
            }
        }
    }

    private hotspotCounter: number = 0;
    protected hotspotClick($event: ng.IAngularEvent, hotspot: Hotspot) {
        this.clearHotspots();
        hotspot.open = true;
        this.hotspotCounter++;
        hotspot.$zindex = this.hotspotCounter;

        let hSceneNode = hotspot.$parentConfigurator.$uiConfigurator.$sceneNode as kb3d.SceneNode;
        let handlers = hSceneNode._manager.hotspotClickHandlers.getByKey(hotspot);

        if (hotspot.idCamera) {
            let viewpoint = hSceneNode._manager.getById<kb3d.ViewpointNode>(hotspot.idCamera);
            if (viewpoint) {
                this.viewer.camera.animateToViewpoint(viewpoint, {
                    duration: hotspot.animationDuration,
                });
            }
        }

        for (let handler of handlers) {
            let clickArgs: IHotspotClickArgs = { propagate: false };
            handler(clickArgs);
        }

        // some hotspot popups start off the screen because the height can't be calculated until it's shown
        // so we need to offload to recalculate the hotspot position after the digest
        this.$timeout(() => {
            if (hotspot.target && hotspot.targetNode && hotspot.visible) {
                let targetNode = hSceneNode._manager.getById<kb3d.MeshNode>(hotspot.targetNode);
                if (targetNode) {
                    let pos = this.viewer.camera.getScreenBoundingBoxOfNode(targetNode);
                    if (pos) {
                        this.positionHotspot(pos, hotspot);
                    }
                }
            }
        });
    }

    protected hotspotCloseClick($event: ng.IAngularEvent, hotspot: Hotspot) {
        $event.stopPropagation();
        hotspot.open = false;
    }

    public getRuleArgs(ruleType: eRuleType): Kb3dSceneRuleArgs {
        let args: Kb3dSceneRuleArgs;
        if (ruleType == eRuleType.function) {
            args = new Kb3dSceneFunctionRuleArgs(this);
        } else {
            args = new Kb3dSceneRuleArgs(this);
        }
        args.environment = this.environment;
        args.baseUrl = this.baseUrl;
        args.configurator = this.sceneConfig.idProduct ? this.uiConfig : this.sceneConfig;
        args.kom = args.configurator.$manager;
        args.parentKom = args.configurator.$parentConfigurator ? args.configurator.$parentConfigurator.$manager : null;
        args.sceneKom = this.sceneConfig.$manager;
        args.sceneNode = this.sceneNode;
        //args.sceneApi = this.sceneApi;
        args.isRender = this.isRender;
        args.renderPass = this.renderPass;
        //args.sceneUtils = this.sceneService.getSceneUtilities(this.sceneApi, ruleType);
        args.clientLanguage = this.clientLanguage;
        args.ruleType = ruleType;
        args.xrActive = this.viewer.ar.xrActive;

        return args;
    }

    public getNestedSceneRuleArgs(uiConfig: Configurator, sceneConfig: Configurator, ruleType: eRuleType) {
        let args = new Kb3dSceneRuleArgs(this);
        args.environment = this.environment;
        args.baseUrl = this.baseUrl;
        args.configurator = uiConfig;
        args.kom = args.configurator.$manager;
        args.parentKom = args.configurator.$parentConfigurator ? args.configurator.$parentConfigurator.$manager : null;
        args.sceneKom = sceneConfig.$manager;
        args.sceneNode = uiConfig.$sceneNode;
        //args.sceneApi = this.sceneApi;
        args.isRender = this.isRender;
        args.renderPass = this.renderPass;
        //args.sceneUtils = this.sceneService.getSceneUtilities(this.sceneApi, ruleType);
        args.clientLanguage = this.clientLanguage;
        args.ruleType = ruleType;
        args.xrActive = this.viewer.ar.xrActive;

        return args;
    }
}

export class Kb3dSceneRuleArgs implements ISceneRuleArgs {
    constructor(public kbv: Kb3dViewer) {
        this.isMobile = kbv.isMobile;
    }
    public environment: eEnvironment;
    public baseUrl: string;
    public configurator: Configurator;
    public kom: KbObjectManager;
    public parentKom: KbObjectManager;
    public sceneKom: KbObjectManager;
    public sceneNode: kb3d.SceneNode;
    public ruleType: eRuleType;
    public isRender: boolean;
    public renderPass: string;
    public isMobile: boolean;
    public xrActive: boolean;
    public clientLanguage: string;
    public logs: ClientLogger = new ClientLogger();
    public upload() {
        //return this.viewer.upload();
    }

    public sendMessage(msg: IMessage) {
        this.kbv.sendMessage(msg);
    }
    public selectPage(page) {
        if (this.kbv.navigateToElement) {
            this.kbv.navigateToElement(page);
        }
    }
    public convertCurrency(obj) {
        return null;
    }
    public getTables(args: IGetTableRuleArgs) {
        return new KPromise(args.ids.map(tid => this.kbv.tableArraysDb[tid]));
    }

    public getById(id: string) {
        return this.sceneNode._manager.getById(id);
    }

    public getByName(name: string) {
        const node = this.sceneNode._manager.getFirstNodeByName(name, true);
        if (!node) {
            console.warn(`No nodes "${name}" found`);
        }
    }

    public getIdByName(name: string) {
        let node = this.sceneNode._manager.getNodeByIdOrName(name);
        if (!node) {
            console.warn(`No nodes "${name}" found`);
        } else {
            return node.id;
        }
    }

    public getParent(n: string | kb3d.Kb3dObject) {
        let node = this.sceneNode._manager.getNodeByIdOrName(n);
        if (node) {
            return node._parent;
        }
    }

    public getNestedPathTo(node: kb3d.Kb3dObject) {
        return this.sceneNode.getNestedPathTo(node);
    }

    public getNestedPathInConfigurator(...configs: (Configurator | string)[]) {
        let m = this.sceneNode._manager;

        const lastItem = configs.pop(); // Last item can be a node ID or name as well
        let path = configs.reduce((result, config) => {
            if (typeof config === 'string') {
                result += config;
                const configurator = this.kom.getByName(config);
                if (configurator instanceof Configurator && configurator.$sceneNode) {
                    m = configurator.$sceneNode._manager;
                }
            } else {
                result += config.name;
                if (config.$sceneNode) {
                    m = config.$sceneNode._manager;
                }
            }

            return result + '/';
        }, '');

        if (typeof lastItem === 'string') {
            const lastConfig = this.kom.getByName(lastItem, false);
            if (lastConfig instanceof Configurator) {
                if (!lastConfig.$sceneNode) {
                    console.warn('No Scene on configurator ' + lastConfig.name);
                    return null;
                }
                path += lastConfig.name + '/' + lastConfig.$sceneNode.id;
            } else {
                const kb3dNode = m.getFirstNodeByName(lastItem);
                if (kb3dNode) {
                    path += kb3dNode.id;
                } else {
                    path += lastItem;
                }
            }
        } else {
            const lastConfig = lastItem;
            if (!lastConfig.$sceneNode) {
                console.warn('No Scene on configurator ' + lastConfig.name);
                return null;
            }
            path += lastConfig.name + '/' + lastConfig.$sceneNode.id;
        }

        return '@' + path;
    }

    public setParent(opts: ISetParentArgs) {
        const manager = this.sceneNode._manager;
        const item = manager.getNodeByIdOrName(opts.node) as Kb3dObject;
        const newParentNode = opts.newParent ? manager.getNodeByIdOrName(opts.newParent, false) : undefined;
        const transform =
            opts.preservePosition && isSpaceNode(item)
                ? this.kbv.viewer.getTransformSpaceDifference(item, newParentNode)
                : undefined;

        let clickHandlers: Record<string, kb3d.Kb3dPointerHandler[]> | undefined;
        let doubleClickHandlers: Record<string, kb3d.Kb3dPointerHandler[]> | undefined;
        let draggables: Record<string, kb3d.IDraggable[]> | undefined;

        if (item._parent) {
            if (isSpaceNode(item) || isAnnotation(item)) {
                clickHandlers = manager.clickHandlers.deleteByKey(item);
                doubleClickHandlers = manager.doubleClickHandlers.deleteByKey(item);
                draggables = manager.draggables.deleteByKey(item);
            }

            item._parent.removeChildren(item);
        }

        return this.kbv.viewer.forceAndWaitForNextRender().then(() => {
            if (transform && isSpaceNode(item)) {
                item.setRotationQuaternion(transform.rotationQuaternion);
                item.scaling = transform.scale;
                item.position = transform.translation;
            }
            newParentNode.addChildren(item);
            item.calculateParents();

            if (isSpaceNode(item) || isAnnotation(item)) {
                if (clickHandlers) {
                    for (const tag in clickHandlers) {
                        for (const handler of clickHandlers[tag]) {
                            manager.clickHandlers.add(item, tag, handler);
                        }
                    }
                }
                if (doubleClickHandlers) {
                    for (const tag in doubleClickHandlers) {
                        for (const handler of doubleClickHandlers[tag]) {
                            manager.doubleClickHandlers.add(item, tag, handler);
                        }
                    }
                }
                if (draggables) {
                    for (const tag in draggables) {
                        for (const handler of draggables[tag]) {
                            manager.draggables.add(item, tag, handler);
                        }
                    }
                }
            }
        });

        // const newNode = this.clone({ node: item, parent: newParentNode });
    }

    public setProperty(args: IKb3dSetPropertyArgs) {
        let m = this.sceneNode._manager;
        let nodes = new Array<kb3d.Kb3dObject>();
        let id = args.id as any;
        if (args.nested) {
            nodes.push(this.getNestedSceneNode(args.nested));
        } else if (id && (id instanceof kb3d.Kb3dObject || id === this.kbv.viewer.camera.activeCamera)) {
            nodes.push(id);
        } else {
            nodes = m.getNodesByIdOrName(args.id, true);
        }

        if (nodes.length === 0) {
            console.warn(`No nodes "${args.id}" found`);
        }

        if (args.feature) {
            nodes = nodes.selectMany(n => {
                return n instanceof MaterialNode
                    ? n.getTextureByName(args.feature)
                    : n instanceof SpaceNode
                    ? n.getFeatureByName(args.feature)
                    : null;
            });
        }

        for (let node of nodes) {
            if (args.subProp && !kb3d.Token.withKey(args.propType as any).isPrimitive()) {
                //cover the case of TextureNodes inside a material
                node[args.prop][args.subProp] = args.value;
            } else {
                let val = this.normalizePropValue(node, args);
                node[args.prop] = val;
            }
        }
    }

    public getProperty(args: IKb3dGetPropertyArgs) {
        let m = this.sceneNode._manager;
        let id = args.id as any;
        let node: Kb3dObject;
        if (id && (id instanceof kb3d.Kb3dObject || id === this.kbv.viewer.camera.activeCamera)) {
            node = id;
        } else {
            node = m.getNodeByIdOrName(args.id, true);
        }

        if (args.feature && node instanceof MaterialNode) {
            node = node.getTextureByName(args.feature);
        }

        if (args.feature && node instanceof SpaceNode) {
            node = node.getFeatureByName(args.feature);
        }

        if (node) {
            let val = args.prop ? node[args.prop] : node;
            if (args.prop && args.subProp) val = val[args.subProp];
            return val;
        } else {
            console.warn(`No nodes "${args.id}" found`);
        }
    }

    protected normalizePropValue(node: kb3d.Kb3dObject, args: IKb3dSetPropertyArgs): any {
        if (!args.subProp && args.value == null) {
            return null; // Return null as-is if applying directly to the prop
        }
        if (!args.subProp && args.propType && args.propType.isEqual(kb3d.Token.KbColor.key)) {
            return this.convertColor(args.value, eColorFormat.kbcolor);
        }
        if (args.subProp) {
            if (kb3d.Token.withKey(args.propType as any).isPrimitive()) {
                if (args.value == null) {
                    throw Error(`Cannot set component of ${args.propType} to null (on ${node.name}/${args.prop})`);
                }
                let currentVal = node[args.prop];
                let newVal = { ...currentVal, [args.subProp]: args.value };
                return this.sceneNode._manager.create(kb3d.Token.withKey(args.propType as any), newVal);
            } /**else { //it's not a primitive but has a sub property
                node[args.prop][args.subProp] = args.value;
                return node[args.prop];
            }*/
        }

        return args.value;
    }

    protected getNestedSceneNode(nestedNameOrInstance: string | Configurator): kb3d.SceneNode {
        let nestedConfig: Configurator;
        if (typeof nestedNameOrInstance === 'string' || nestedNameOrInstance instanceof String) {
            //if it's a string find the nested configurator
            nestedConfig = this.configurator.$manager.getByName<Configurator>(nestedNameOrInstance as string);
        } else if (nestedNameOrInstance instanceof Configurator) {
            nestedConfig = nestedNameOrInstance;
        }

        if (!nestedConfig) {
            throw Error(`could not find nested configurator with name '${nestedNameOrInstance}'`);
        }

        return nestedConfig.$sceneNode;
    }

    public convertColor(from: any, toFormat: eColorFormat = eColorFormat.kbcolor) {
        if (toFormat == eColorFormat.kbcolor && from instanceof kb3d.KbColor) {
            return from;
        }

        if (from instanceof kb3d.KbColor) {
            from = from.toIntObject();
        }

        let color = new Color(from);
        let result: any;
        if (toFormat == eColorFormat.hex) {
            result = color.toHexString();
        } else if (toFormat == eColorFormat.hsl) {
            result = color.toHsl();
        } else if (toFormat == eColorFormat.hslString) {
            result = color.toHslString();
        } else if (toFormat == eColorFormat.hsv) {
            result = color.toHsv();
        } else if (toFormat == eColorFormat.hsvString) {
            result = color.toHsvString();
        } else if (toFormat == eColorFormat.rgb) {
            result = color.toRgb();
        } else if (toFormat == eColorFormat.rgbString) {
            result = color.toRgbString();
        } else if (toFormat == eColorFormat.kbcolor) {
            let rgb = color.toRgb();
            result = kb3d.KbColor.FromIntObject(rgb);
        }

        return result;
    }

    public colorOperation(args: IColorOperationArgs) {
        if (args.color instanceof KbColor) {
            args.color = args.color.toIntObject();
        }

        let color = new Color(args.color);
        color = color[args.operation](args.amount);

        return KbColor.FromIntObject(color.toRgb());
    }

    public addClickHandler(args: IKb3dAddClickHandlerArgs) {
        args.handler = this.digestEvent(args.handler); //the viewer runs the event, so we need to wrap it for the angular digest
        this.sceneNode._manager.clickHandlers.add(args.id, this.getHandlerTag(args.tag), args.handler);
    }

    public addDoubleClickHandler(args: IKb3dAddClickHandlerArgs) {
        args.handler = this.digestEvent(args.handler); //the viewer runs the event, so we need to wrap it for the angular digest
        this.sceneNode._manager.doubleClickHandlers.add(args.id, this.getHandlerTag(args.tag), args.handler);
    }

    public addDraggable(args: kb3d.IDraggable & { tag?: string }) {
        this.sceneNode._manager.draggables.add(args.id, this.getHandlerTag(args.tag), args);
    }

    public addHotspotClickHandler(args: IKb3dAddHotspotClickHandlerArgs) {
        //no $digest wrap needed for hotspot handler because the event is already coming from angular
        this.sceneNode._manager.hotspotClickHandlers.set(args.hotspot, this.getHandlerTag(args.tag), args.handler);
    }

    public addBoundboxChangeHandler(args: IKb3dAdBoundingboxWatcherArgs) {
        args.handler = this.digestEvent(args.handler); //the viewer runs the event, so we need to wrap it for the angular digest
        this.sceneNode._manager.boundingboxChangeHandlers.add(args.id, this.getHandlerTag(args.tag), {
            handler: args.handler,
            mode: args.mode,
        });
    }

    protected getHandlerTag(argsTag: string) {
        if (this.ruleType == eRuleType.scene) {
            //scene rule handlers get a tag of 'scene' automatically and are removed automatically
            return eRuleType.scene;
        } else {
            //those added by other rules get the tag given by the snap user
            return argsTag;
        }
    }

    public clearHandlers(args: IKb3dClearHandlerArgs) {
        let handlerMap: kb3d.TagArrayMap<any, any>;
        let m = this.sceneNode._manager;
        if (args.handlerType == kb3d.eHandler.click) {
            handlerMap = m.clickHandlers;
        } else if (args.handlerType == kb3d.eHandler.doubleClick) {
            handlerMap = m.doubleClickHandlers;
        } else if (args.handlerType == kb3d.eHandler.draggable) {
            handlerMap = m.draggables;
        } else if (args.handlerType == kb3d.eHandler.hotspot) {
            handlerMap = m.hotspotClickHandlers;
        }

        if (handlerMap) {
            if (args.tag) {
                handlerMap.deleteByTag(args.tag);
            } else {
                handlerMap.clear();
            }
        }
    }

    public animate(args: IKb3dAnimateArgs) {
        let nodes = new Array<Kb3dObject>();
        if (args.node) {
            nodes = [args.node];
        } else {
            nodes = this.sceneNode._manager.getNodesByIdOrName(args.id, true);
        }
        let promises = new Array<Promise<any>>();
        for (let node of nodes) {
            let toValue = this.normalizePropValue(node, args);
            let a = {
                ...args,
                toValue: toValue,
                node: node,
            } as kb3d.IAnimation;

            promises.push(this.kbv.viewer.animation.queue(a));
        }
        return Promise.all(promises);
    }

    public frameCamera(args: kb3d.IFramingOptions & { node: kb3d.SpaceNode }) {
        this.kbv.viewer.camera.frameCamera(args.node, args);
    }

    public animateCamera(args: kb3d.ICameraAnimationOptions & { viewpoint: kb3d.ViewpointNode }) {
        return this.kbv.viewer.camera.animateToViewpoint(args.viewpoint, args);
    }

    public create<T>(args: IKb3dCreateNodeArgs): T {
        let token = kb3d.Token.withKey(args.type as any);
        let n = this.sceneNode._manager.create(token, args.args);
        this.addToParent(n, args.parent, token);

        return n;
    }

    public createSketchPath(args: IKb3dCreateSketchPathArgs): kb3d.SketchPath {
        let n = this.sceneNode._manager.create(kb3d.Token.SketchPath, args.args);
        this.addToParent(n, args.parent, kb3d.Token.SketchPath);
        const controlPoints = args.controlPoints.filter(input => input && input instanceof kb3d.SketchControlPoint);
        n.addChildren(...controlPoints);

        return n;
    }

    public clone(args: IKb3dCloneNodeArgs): Kb3dObject {
        let o = args.node as Kb3dObject;
        let clone = this.sceneNode._manager!.clone(o);
        for (let p in args.args) {
            clone[p] = args.args[p];
        }
        let token = kb3d.Token.withKey(clone.$type);
        this.addToParent(clone, args.parent, token);

        return clone;
    }

    public delete(node: Kb3dObject) {
        if (node && node._parent) {
            node._parent.removeChildren(node);
        }
    }

    public getChildrenOfType(args: IKb3dGetChildrenOfTypeArgs): Array<kb3d.Kb3dObject> {
        let n = args.node;
        if (n instanceof kb3d.Kb3dObject) {
            let tok = kb3d.Token.withKey(args.type as any);
            return n.filterByType(tok);
        } else {
            return [];
        }
    }

    public searchByName(name: string) {
        return this.sceneNode._manager.getNodesByIdOrName(name, true);
    }

    public getActiveCamera() {
        return this.kbv.viewer.camera.activeCamera;
    }

    public highlight(args: IKb3dHighlightArgs) {
        this.kbv.viewer.forceAndWaitForNextRender().then(() => {
            //wait for next render to make sure the mesh exists before highlighting
            let nodes = this.sceneNode._manager.getNodesByIdOrName(args.id);
            for (let n of nodes) {
                if (n instanceof kb3d.SpaceNode) {
                    if (args.on) {
                        this.kbv.viewer.highlighter.add(n, this.convertColor(args.color, eColorFormat.kbcolor));
                    } else {
                        this.kbv.viewer.highlighter.remove(n);
                    }
                }
            }
        });
    }

    public clearHighlights() {
        this.kbv.viewer.highlighter.clearAll();
    }

    public activateGizmo(args: IKb3dActivateGizmoArgs) {
        let attachMesh: kb3d.SpaceNode;
        // Rotation with override space node doesn't function well. Disable that functionality so people don't try to use it,
        // Until we can swap axis/angle gizmo in and fix the transform space conversion
        if (args.gizmo === 'rotate') {
            delete args.overrideSpaceNode;
        }
        if (args.overrideSpaceNode) {
            attachMesh = args.overrideSpaceNode;
        } else {
            attachMesh = args.node;
        }
        const mesh = args.node as kb3d.SpaceNode;
        this.kbv.viewer.gizmos.clearGizmo();
        if (args.gizmo === 'rotate') {
            const updates = this.kbv.viewer.gizmos.rotate([attachMesh], undefined, undefined, args.axisVisibility);
            let startQ = KbQuaternion.FromRotationAxis(mesh.rotationAxis, mesh.rotationAngle);
            let startEuler = mesh.eulerRotation.clone();
            updates.update.subscribe(updates => {
                mesh._primeMover = true;
                updates.forEach((update, index) => {
                    mesh.position = mesh.position.add(update.position);
                    let qGizmo: KbQuaternion = KbQuaternion.FromYawPitchRoll(update.rotation);

                    if (!args.overrideSpaceNode) {
                        if (mesh.useEuler) {
                            mesh.eulerRotation = mesh.eulerRotation.add(update.rotation);
                        } else {
                            const q = KbQuaternion.FromRotationAxis(mesh.rotationAxis, mesh.rotationAngle);
                            const z = KbQuaternion.FromYawPitchRoll(update.rotation);
                            const result = q.multiply(z);
                            mesh.rotationAngle = result.getAngle();
                            mesh.rotationAxis = result.getAxis();
                        }
                        return;
                    } else {
                        const bnode = this.kbv.viewer.getRendererNode(args.node as SpaceNode);
                        if (bnode) {
                            const inSpace = bnode.parent ? bnode.parent.computeWorldMatrix(true) : undefined;
                            const transform = applyTransformToSpace(update.worldTransformMatrix, inSpace);
                            qGizmo = transform.rotation;
                        }

                        if (mesh.useEuler) {
                            mesh.eulerRotation = startEuler.add(qGizmo.toEuler());
                        } else {
                            const result = startQ.multiply(qGizmo);
                            mesh.rotationAngle = result.getAngle();
                            mesh.rotationAxis = result.getAxis();
                        }
                    }
                });
            });

            updates.totalDelta.subscribe(() => {
                startQ = KbQuaternion.FromRotationAxis(mesh.rotationAxis, mesh.rotationAngle);
                startEuler = mesh.eulerRotation.clone();
                if (args.dropped) {
                    args.dropped();
                }
                mesh._primeMover = false;
            });
        } else if (args.gizmo === 'position') {
            //update attachMesh 
            let updates = null
            if (args.overrideSpaceNode && args.overrideSpaceNode instanceof SpaceNode) {
                const rotationQuaternion = args.overrideSpaceNode.getRotationQuaternion();
            updates = this.kbv.viewer.gizmos.position(args.node, undefined, args.axisVisibility, rotationQuaternion.toQuat());
            } else {
                updates = this.kbv.viewer.gizmos.position(attachMesh, undefined, args.axisVisibility);
            }
            if (updates) {
                updates.update.subscribe(newPosition => {
                    mesh._primeMover = true;
                    mesh.position = mesh.position.add(newPosition);
                });
            }

            if (args.dropped) {
                updates.totalDelta.subscribe(() => {
                    args.dropped();
                    mesh._primeMover = false;
                });
            }
        }

        function convertSpace(gizmoSpace: TransformNode | undefined, nodeSpace: TransformNode | undefined) {}
    }

    public clearGizmos() {
        this.kbv.viewer.gizmos.clearGizmo();
    }

    public activateSketchEditor(args: IKb3dActivateSketchArgs) {
        this.clearSketchEditor();
        if (args.node instanceof kb3d.SketchNode) {
            this.kbv.viewer.sketchEditor.startOnPlane(
                args.node,
                {
                    normal: (args.axis as kb3d.eAxis) || args.node.normal,
                    intersect: args.intersect || args.node.position,
                },
                {
                    snapSize: args.snapSize || undefined,
                }
            );
            if (args.modified) {
                this.kbv.viewer.sketchEditor.mouseUp.subscribe(() => {
                    this.kbv.viewer.forceAndWaitForNextRender().then(() => {
                        args.modified();
                    });
                });
            }
        }
    }

    public clearSketchEditor() {
        this.kbv.viewer.sketchEditor.clear();
    }

    public overwriteSketchWithSVG(args) {
        if (args.svgImage) {
            fetch(args.svgImage)
                .then(resp => resp.blob())
                .then(blob => this.kbv.viewer.applySvgToSketch(args.node, blob))
                .catch(e => {
                    console.warn('Could not fetch image ' + args.svgImage, e);
                });
        }
    }

    public getAbsolutePosition(node: string | kb3d.SpaceNode | kb3d.Connector) {
        let n = this.sceneNode._manager.getNodeByIdOrName(node);
        if (n) {
            let bnode = this.kbv.viewer.getRendererNode(n as SpaceNode);
            if (bnode) {
                return kb3d.KbVector.From(bnode.getAbsolutePosition());
            }
        }
        return kb3d.KbVector.Zero();
    }

    public getSketchPathLength(path: kb3d.SketchPath) {
        const pointsArray: [number, number, number][] = [];
        path._points.forEach(point => {
            pointsArray.push([point.x, point.y, point.z]);
        });

        return computeLength(pointsArray);

        function computeLength(path: number[][]): number {
            let l = 0;
            for (let i = 1; i < path.length; i++) {
                const point = path[i];
                const prevPoint = path[i - 1];
                l += Math.hypot(...[point[0] - prevPoint[0], point[1] - prevPoint[1], point[2] - prevPoint[2]]);
            }
            return l;
        }
    }

    public getBoundingBox(node: kb3d.SpaceNode, space: 'world' | 'local' = 'world') {
        if (space === 'world') {
            return this.kbv.viewer.bbCache.getWorld(node);
        } else {
            return this.kbv.viewer.bbCache.refreshAndGet(node);
        }
    }

    public nodesIntersect(node1: kb3d.SpaceNode, node2: kb3d.SpaceNode) {
        return this.kbv.viewer.nodesIntersect(node1, node2);
    }

    public copyPropertiesTo(fromNode: kb3d.Kb3dObject, toNode: kb3d.Kb3dObject) {
        if (this.sceneNode && this.sceneNode._manager) {
            this.sceneNode._manager.assign(toNode, fromNode, { copyingToDifferentNode: true });
        }
    }

    public getMateChain(node: kb3d.SpaceNode) {
        const activeMates = this.kbv.viewer.getActiveMates();
        return [node, ...Array.from(kb3d.getNodeChain(node, activeMates))];
    }

    public rotate(args: { node: kb3d.SpaceNode; axis: kb3d.KbVector; angle: number }) {
        args.node.rotate(args.axis, args.angle);
    }

    public callSceneFunction(args: IKb3dCallSceneFunctionArgs, config: Configurator) {
        if (!this.kbv) {
            console.warn(`Scene function ${args.name} was called but a scene is not loaded.`);
            return Promise.reject();
        }
        return this.kbv.runSceneFunction(args, config).then(r => r.parameters.returnValue);
    }

    public waitForNextRender() {
        return this.kbv.viewer.forceAndWaitForNextRender();
    }

    public convertAxisAngleToEuler(axis: kb3d.KbVector, angle: number) {
        const q = kb3d.KbQuaternion.FromRotationAxis(axis, angle);
        return q.toEuler().round(0.0000001); //round out floating point errors
    }

    public getGlobalMaterialNode(id: string) {
        return new Promise((resolve, reject) =>
            this.kbv.viewer.rendererManager.material
                .loadGlobalMaterial(id, this.sceneNode._manager!.services)
                .subscribe(resolve, reject)
        );
    }

    protected addToParent(node: Kb3dObject, parent: Kb3dObject, childToken: kb3d.Token) {
        if (parent) {
            parent.addChildren(node);
        }
    }

    protected digestEvent(ev: (a: any) => void) {
        return a => {
            ev(a);
            this.kbv.$scope.$digest();
        };
    }
}

export class Kb3dSceneFunctionRuleArgs extends Kb3dSceneRuleArgs {
    constructor(public kbv: Kb3dViewer) {
        super(kbv);
    }
    public parameters: any;
    public returnValue: any;
}
